import Cookies from 'js-cookie';

import type { AviosAuthUserSession } from './types';

const AUTH_STATE_STORAGE_KEY = 'avios-auth:state';
const AUTH_STATE_CHANNEL_NAME = 'avios-auth:state-changed';
const AUTH_STATE_SYNC_STORAGE_KEY = 'avios-auth:state-changed-event';

// Read from the cookie, not the URL - the URL can still be mid-redirect when this is called.
const getCurrentLocale = (): string | undefined => Cookies.get('AVIOS_OPCO_LANG');

type AuthStateChangeMessage =
  | {
      type: 'auth-state-changed';
      state: string;
      locale?: string;
    }
  | {
      type: 'preferences-changed';
      locale?: string;
    };

export type AuthStateChangePayload = {
  locale?: string;
};

const getAuthStateMarker = (user: AviosAuthUserSession | null | undefined): string => {
  if (!user) {
    return 'unauthenticated';
  }

  const opco = Cookies.get('AVIOS_OPCO') ?? 'unknown-opco';
  const userId = user.tokenContent.sub ?? user.tokenContent.membershipNumber;

  return `authenticated:${opco}:${userId}`;
};

const isAuthStateChangeMessage = (value: unknown): value is AuthStateChangeMessage =>
  typeof value === 'object' &&
  value !== null &&
  (((value as AuthStateChangeMessage).type === 'auth-state-changed' && typeof (value as { state?: unknown }).state === 'string') ||
    (value as AuthStateChangeMessage).type === 'preferences-changed');

const getStoredAuthStateMarker = (): string | null => {
  try {
    return window.sessionStorage.getItem(AUTH_STATE_STORAGE_KEY);
  } catch {
    return null;
  }
};

const storeAuthStateMarker = (state: string): void => {
  try {
    window.sessionStorage.setItem(AUTH_STATE_STORAGE_KEY, state);
  } catch {
    // Ignore storage failures; cross-tab sync remains best effort.
  }
};

const broadcastAuthStateChange = (state: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const message: AuthStateChangeMessage = { type: 'auth-state-changed', state, locale: getCurrentLocale() };

  if (typeof BroadcastChannel !== 'undefined') {
    try {
      const channel = new BroadcastChannel(AUTH_STATE_CHANNEL_NAME);
      channel.postMessage(message);
      channel.close();
      return;
    } catch {
      // Fall back to localStorage when BroadcastChannel is unavailable or blocked.
    }
  }

  try {
    window.localStorage.setItem(AUTH_STATE_SYNC_STORAGE_KEY, JSON.stringify(message));
    window.localStorage.removeItem(AUTH_STATE_SYNC_STORAGE_KEY);
  } catch {
    // Ignore storage failures; cross-tab sync remains best effort.
  }
};

export const broadcastAuthStateChangeOrRefresh = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const message: AuthStateChangeMessage = { type: 'preferences-changed', locale: getCurrentLocale() };

  if (typeof BroadcastChannel !== 'undefined') {
    try {
      const channel = new BroadcastChannel(AUTH_STATE_CHANNEL_NAME);
      channel.postMessage(message);
      channel.close();
      return;
    } catch {
      // Fall back to localStorage when BroadcastChannel is unavailable or blocked.
    }
  }

  try {
    window.localStorage.setItem(AUTH_STATE_SYNC_STORAGE_KEY, JSON.stringify(message));
    window.localStorage.removeItem(AUTH_STATE_SYNC_STORAGE_KEY);
  } catch {
    // Ignore storage failures; cross-tab sync remains best effort.
  }
};

export const synchronizeAuthState = (user: AviosAuthUserSession | null | undefined): boolean => {
  const nextAuthState = getAuthStateMarker(user);
  const previousAuthState = getStoredAuthStateMarker();

  storeAuthStateMarker(nextAuthState);

  if (previousAuthState !== null && previousAuthState !== nextAuthState) {
    broadcastAuthStateChange(nextAuthState);
    return true;
  }

  return false;
};

export const subscribeToAuthStateChanges = (onAuthStateChange: (payload: AuthStateChangePayload) => void): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const handleAuthStateChange = (message: unknown): void => {
    if (!isAuthStateChangeMessage(message)) {
      return;
    }

    if (message.type === 'auth-state-changed') {
      const currentMarker = getStoredAuthStateMarker();

      storeAuthStateMarker(message.state);

      // This tab already knows about this state. Nothing to resync.
      if (currentMarker === message.state) {
        return;
      }
    }

    onAuthStateChange({ locale: message.locale });
  };

  const handleStorageEvent = (event: StorageEvent): void => {
    if (event.key !== AUTH_STATE_SYNC_STORAGE_KEY || !event.newValue) {
      return;
    }

    try {
      handleAuthStateChange(JSON.parse(event.newValue));
    } catch {
      // Ignore malformed storage events.
    }
  };

  window.addEventListener('storage', handleStorageEvent);

  let channel: BroadcastChannel | null = null;

  if (typeof BroadcastChannel !== 'undefined') {
    try {
      channel = new BroadcastChannel(AUTH_STATE_CHANNEL_NAME);
      channel.onmessage = (event: MessageEvent<unknown>) => handleAuthStateChange(event.data);
    } catch {
      // The storage listener remains as the fallback.
    }
  }

  return () => {
    channel?.close();
    window.removeEventListener('storage', handleStorageEvent);
  };
};
